import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { isNodeWith } from '@shure/shared/typescript/data-access/system-api-graphql';

import { CoverageAudioPassiveSpeakerApiService } from '../api/coverage-audio-passive-speaker-api.service';
import { PassiveSpeaker } from '../api/models';

import {
	DeviceCoveragePassiveSpeakerQueryGQL,
	PassiveSpeakerSubscriptionGQL
} from './graphql/generated/device-coverage-audio-passive-speaker.generated';
import { mapDeviceCoveragePassiveSpeakers } from './mappers/coverage-audio-channel-passive-speaker.mapper';

@Injectable()
export class SysApiCoverageAudioPassiveSpeakerApiService implements CoverageAudioPassiveSpeakerApiService {
	public readonly passiveSpeakerGqlSubscriptions: CombiningSubscriptionManager;

	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		subscriptionManagerService: SubscriptionManagerService,
		private readonly passiveSpeakerQuery: DeviceCoveragePassiveSpeakerQueryGQL,
		passiveSpeakerSubscriptionGQL: PassiveSpeakerSubscriptionGQL
	) {
		this.logger = logger.createScopedLogger('SysApiCoverageAudioPassiveSpeakerApiService');

		this.passiveSpeakerGqlSubscriptions = new CombiningSubscriptionManager(
			{
				name: 'SysApiCoverageApiService:PassiveSpeaker',
				changeTypes: [
					NodeChangeType.PassiveSpeakerCoveragePosition,
					NodeChangeType.PassiveSpeakerRotation,
					NodeChangeType.PassiveSpeakerHeight,
					NodeChangeType.PassiveSpeakerListenerHeight
				],
				subscriptionGql: passiveSpeakerSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getAudioChannelPassiveSpeaker$(passiveSpeakerId: string): Observable<PassiveSpeaker> {
		return this.passiveSpeakerQuery
			.watch({ variables: { nodeId: passiveSpeakerId }, errorPolicy: 'all' })
			.valueChanges.pipe(
				waitForGQLCompleteQueryOrThrow(),
				map((query) => {
					if (!isNodeWith(query.data?.node, 'isPassiveSpeakerNode')) {
						throw ApolloQueryErrorMapper.getError(query);
					}
					const passiveSpeaker = mapDeviceCoveragePassiveSpeakers(query.data.node);
					this.logger.trace('getAudioPassiveSpeaker$()', 'Done', { passiveSpeakerId });
					return passiveSpeaker;
				})
			);
	}
}
